import { registerSessionCleanup } from '../../../../services/session/sessionCleanup';

/**
 * In-memory LRU cache of the annex previews loaded during the session.
 *
 * Reopening an annex reuses the loaded file instead of requesting it again
 * from core, which reads it from storage on every request. Entries are keyed
 * by the URL without its signature: the content behind an access_key never
 * changes and the signature only authorizes a download that already happened.
 * The cache belongs to the signed-in user, so it is cleared when the session
 * ends (see registerSessionCleanup).
 */
const PREVIEW_CACHE_MAX_ENTRIES = 10;
const PREVIEW_CACHE_MAX_BYTES = 150 * 1024 * 1024;
const previewCache = new Map();
let cachedBytes = 0;

/**
 * Cache key of a URL: its origin and path, without the signature.
 *
 * @param {string} url
 * @returns {string}
 */
const getCacheKey = (url) => {
  try {
    const { origin, pathname } = new URL(url);
    return origin + pathname;
  } catch {
    return url;
  }
};

/**
 * Removes the entry of the key and releases its object URL.
 *
 * @param {string} key
 */
const evictKey = (key) => {
  const entry = previewCache.get(key);
  if (entry) {
    URL.revokeObjectURL(entry.objectUrl);
    previewCache.delete(key);
    cachedBytes -= entry.size;
  }
};

/**
 * Cached preview of the URL without changing its LRU position.
 *
 * @param {string} url
 * @returns {{objectUrl: string, size: number}|null}
 */
export const peekCachedPreview = (url) => previewCache.get(getCacheKey(url)) || null;

/**
 * Cached preview of the URL, marked as the most recently used entry.
 *
 * @param {string} url
 * @returns {{objectUrl: string, size: number}|null}
 */
export const getCachedPreview = (url) => {
  const key = getCacheKey(url);
  const entry = previewCache.get(key);
  if (!entry) return null;
  previewCache.delete(key);
  previewCache.set(key, entry);
  return entry;
};

/**
 * Stores the preview of the URL, evicting the least recently used entries
 * while the cache exceeds its entry or size limit. The newest entry is always
 * kept, even when it alone exceeds the size limit.
 *
 * @param {string} url
 * @param {{objectUrl: string, size: number}} entry
 */
export const cachePreview = (url, entry) => {
  const key = getCacheKey(url);
  evictKey(key);
  previewCache.set(key, entry);
  cachedBytes += entry.size;
  while (
    previewCache.size > 1 &&
    (previewCache.size > PREVIEW_CACHE_MAX_ENTRIES || cachedBytes > PREVIEW_CACHE_MAX_BYTES)
  ) {
    evictKey(previewCache.keys().next().value);
  }
};

/**
 * Removes the preview of the URL and releases its object URL.
 *
 * @param {string} url
 */
export const evictCachedPreview = (url) => evictKey(getCacheKey(url));

/**
 * Removes every preview and releases their object URLs.
 */
export const clearAnnexPreviewCache = () => {
  Array.from(previewCache.keys()).forEach(evictKey);
};

registerSessionCleanup(clearAnnexPreviewCache);
